/**
 * MOSA-409. Connection health for a Google Sheets panel, on the client.
 *
 * `GET /datasources/:id/health` already decides what this caller is told: the
 * API knows whether they own the panel, and it writes the owner copy or the
 * member copy accordingly. So this module does not re-derive the audience — it
 * carries the shapes, turns each action id into the place it leads, and holds
 * the two calls.
 *
 * The one rule repeated here: a notice with no action renders no button. Only
 * the owner holds the Google connection, so a member who clicked "Reconnect
 * Google" would land on a consent screen that rebinds nothing (QA finding D3
 * on MOSA-554). The API sends `actions: []` to a member, and
 * `actionsFor` refuses to invent one.
 *
 * Types and pure helpers only, the way `sync-state.ts` is; the two calls live
 * in `sheets-health-api.ts`.
 */

import { canonicalSheetsUrl } from "./sheets-link";

/** What a health check concluded. Mirrors `SheetsHealthCode` in the API. */
export type SheetsHealthCode =
  | "healthy"
  | "google_reconnect_required"
  | "google_file_access_lost"
  | "sheet_not_found"
  | "sheet_columns_changed";

export type SheetsHealthActionId =
  | "reconnect_google"
  | "request_access"
  | "pick_again"
  | "restore_in_drive"
  | "connect_another_sheet"
  | "map_columns"
  | "keep_both_columns";

export type SheetsHealthAction = {
  id: SheetsHealthActionId;
  label: string;
  primary: boolean;
};

export type SheetsHealthNotice = {
  code: SheetsHealthCode;
  audience: "owner" | "member";
  title: string;
  body: string;
  actions: SheetsHealthAction[];
};

/** The body of `GET /datasources/:id/health`. */
export type SheetsHealth = {
  code: SheetsHealthCode;
  ok: boolean;
  checkedAt?: string | null;
  notice?: SheetsHealthNotice | null;
  fileTitle?: string | null;
  renamedColumns?: Array<{ from: string; to: string }>;
  unmappedColumns?: string[];
  tab?: string | null;
};

/** Is there anything to show above the grid? */
export const hasHealthProblem = (
  health: SheetsHealth | null | undefined,
): boolean => Boolean(health && !health.ok && health.notice);

/**
 * The actions to render.
 *
 * Empty for a member, and empty for anything the API did not send. A control
 * this app invents for a caller who cannot use it is worse than no control:
 * it reads as "you can fix this", and they cannot.
 */
export const actionsFor = (
  notice: SheetsHealthNotice | null | undefined,
): SheetsHealthAction[] => {
  if (!notice || notice.audience !== "owner") return [];
  return notice.actions ?? [];
};

/** True when this column is read-only until the owner answers the notice. */
export const isColumnLocked = (
  health: SheetsHealth | null | undefined,
  column: string,
): boolean => {
  if (!health || health.code !== "sheet_columns_changed") return false;
  const wanted = column.trim().toLowerCase();
  return (health.unmappedColumns ?? []).some(
    (name) => name.trim().toLowerCase() === wanted,
  );
};

/**
 * Where an action leads, for the ones that are a link rather than a call.
 *
 * `request_access` opens the sheet in Google. That is deliberate: Google's own
 * page is what shows "Request access" and routes the ask to whoever owns the
 * file. OnlyDash cannot grant Drive access and must not pretend to.
 *
 * Returns null for the actions that are a call or an in-app flow:
 * `reconnect_google`, `pick_again`, `connect_another_sheet`, `map_columns` and
 * `keep_both_columns`.
 */
export const actionHref = (
  id: SheetsHealthActionId,
  spreadsheetId: string | null | undefined,
): string | null => {
  if (id === "request_access") {
    return spreadsheetId ? canonicalSheetsUrl(spreadsheetId) : null;
  }
  if (id === "restore_in_drive") {
    return "https://drive.google.com/drive/trash";
  }
  return null;
};

/**
 * The rows the Map dialog shows: the old name, the new name, and one line
 * saying what each choice does to the panel's fields.
 */
export type ColumnMapRow = {
  from: string;
  to: string;
};

export const columnMapRows = (
  health: SheetsHealth | null | undefined,
): ColumnMapRow[] => health?.renamedColumns ?? [];

/** The one line under the choice control, so the owner can tell them apart. */
export const columnChoiceLine = (choice: "map" | "keep_both"): string =>
  choice === "map"
    ? "Keeps one field and moves it onto the new column name. Filters, forms and history keep working."
    : "Adds the new column as a second field and leaves the old one as it is.";
